// biome-ignore lint/style/useNodejsImportProtocol: we use the events module, but biome can't detect it in a monorepo yet
import { EventEmitter } from 'events';
import debounce from 'lodash.debounce';
import * as Y from 'yjs';

import {
    applyVersionedUpdate,
    type ContentOperation,
    createDataSyncEncoder,
    createDataSyncRPCClient,
    type DataSyncDecoderHandlers,
    type DataSyncEncoder,
    type DataSyncRPCClient,
    type DataSyncRPCServiceName,
    dataSyncProtocol,
    getMessageId,
    getYMutationClock,
    requireContentMarker,
    requireContentOperation,
    requirePresence,
    UpdateFormatVersion,
    type YJSInput,
} from '@gitbook/hive-core';
import { type DebugLogger, Logger, reportException, Trace } from '@gitbook/util-debug';
import { assert, assertIsDefined, assertNever, filterOutNullable } from '@gitbook/util-ts';
import { type WebSocket, WebSocketState } from '@gitbook/util-websocket';

import type { HiveClient } from './HiveClient';
import { type HiveDataSyncBrowserCache, HiveDataSyncCacheOrigin } from './HiveDataSyncBrowserCache';
import type { HiveV2Socket } from './HiveV2Socket';

export interface HiveDataSyncPosition {
    type: Y.AbstractType<any>;
    index: number;
    assoc: number;
}

export interface HiveDataSyncPresence<TPresence extends Record<string, string>> {
    clientId: string;
    data: TPresence;
    joinTimestamp: number;
    updateTimestamp: number;
    positions: Record<string, Y.RelativePosition>;
}

export type HiveDataSyncMarker<TData extends Record<string, unknown>> = {
    id: string;
    type: dataSyncProtocol.ContentMarkerType;
    data: TData;
    author: string;
    anchor: Y.RelativePosition;
    focus: Y.RelativePosition;
    content: Uint8Array | null;
};

export enum HiveDataSyncState {
    CLOSED = 'closed',

    /**
     * Client is opening the WebSocket.
     */
    CONNECTING = 'connecting',

    /**
     * WebSocket is open, and initial handshake of doc state is happening.
     */
    HANDSHAKING = 'handshaking',

    /**
     * WebSocket is open and handshake has been done.
     */
    OPEN = 'open',

    ERRORED = 'errored',

    /**
     * The socket was closed due to inactivity timeout.
     * Calling open() while in this state is a no-op.
     */
    INACTIVE = 'inactive',
}

export enum HiveDataSyncUpdateOrigin {
    LOCAL = 'local',
    REMOTE = 'remote',
}

interface HiveDataSyncCurrentPresence<TPresence extends Record<string, string>> {
    data: TPresence;
    positions: Record<string, Y.RelativePosition>;
}

/**
 * Common presence data for all data syncs.
 */
export type HiveGlobalPresence = {
    userId: string;
    userDisplayName: string;
};

/**
 * A socket that can be used to synchronize a Yjs document with a Hive server using the DataSync protocol defined in @gitbook/hive-core.
 */
export class HiveDataSyncClient<
    TPresence extends Record<string, string>,
    TMarkerData extends Record<string, string> = {},
> {
    /** A global id for this data sync. */
    private readonly guid: string;
    private readonly contentId: string;
    private readonly path: string;
    private readonly pathParams: Record<string, string>;
    private readonly logger: DebugLogger;

    /**
     * Current state of the sync.
     * Don't use this directly, prefer the `.state` setter/getter.
     */
    #state: HiveDataSyncState = HiveDataSyncState.CLOSED;

    /**
     * Mutable Yjs document that is synced with the other clients through the backend.
     */
    public doc: Y.Doc;

    /**
     * The type of the main data on the Yjs Doc
     */
    private YType: typeof Y.AbstractType<any>;

    /**
     * Mutable running operation that is synced from the backend.
     */
    public operation: ContentOperation | null = null;

    /**
     * ID allocated by the server for this client.
     * The ID doesn't represent the user, but represents the device / tab.
     * Multiple data-sync in the same tab can share the same client ID.
     */
    public clientId: string | null = null;

    /**
     * Mapping of all presences in the session.
     */
    public presences: HiveDataSyncPresence<TPresence>[];

    /**
     * Mapping of all content markers in the session.
     */
    public markers: HiveDataSyncMarker<TMarkerData>[] = [];

    private client: HiveClient;
    public socket: WebSocket | HiveV2Socket | null = null;
    private encoder: DataSyncEncoder | null = null;
    private cache: HiveDataSyncBrowserCache | undefined = undefined;

    /**
     * State of the presence of the current client.
     */
    private currentPresence: HiveDataSyncCurrentPresence<TPresence> | null = null;

    /**
     * Latest presence before the last transaction run locally.
     */
    private beforeTransactionPresence: HiveDataSyncCurrentPresence<TPresence> | null = null;

    /**
     * Is the client read-only?
     */
    #clientReadOnly: boolean = false;

    /**
     * Is the server forcing the client to be read-only?
     */
    #serverReadOnly: boolean = false;

    /**
     * Does this client need to normalize updates coming from the server?
     */
    #needsClientSideNormalization: boolean = true;

    /**
     * RPC client for content markers.
     */
    #markersClient: dataSyncProtocol.Markers;

    /**
     * Was the client drestroyed and can't be used anymore?
     */
    private destroyed = false;

    /**
     * Singleton for all events.
     */
    public readonly events = new EventEmitter<{
        state: [HiveDataSyncState];
        restorePositions: [unknown];
        update: [string];
        readOnly: [boolean];
        operation: [ContentOperation];
        clientId: [string];
        presences: [];
        markers: [];
    }>();

    /**
     * Shared promises for callers waiting on a specific state.
     *
     * We store this locally to avoid over-subscribing to the EventEmitter.
     */
    private waitForStatePromises: Map<HiveDataSyncState, Promise<void>> = new Map();

    /**
     * Undo/redo to manage the history of the document.
     */
    private undoManager: Y.UndoManager;

    /**
     * Buffer of updates that should be sent to the server.
     */
    private dangerouslyPaused: undefined | Array<Uint8Array>;

    /**
     * Batch of updates to be sent to the server.
     */
    private batchUpdates: Array<Uint8Array> = [];

    /**
     * Debounce function to flush the updates.
     */
    private batchDebounceFlush: ReturnType<typeof debounce> | undefined;

    /**
     * Callback for RPC reply messages.
     */
    private rpcClients: Array<DataSyncRPCClient<any>> = [];

    /**
     * Log of updates applied to the document.
     */
    private readonly updateLog: Array<{
        timestamp: number;
        update: Uint8Array;
        origin: string;
    }> = [];

    /**
     * Messages that are currently waiting their ACK response from the server.
     */
    private readonly messageResolvers: Map<number, MessageResolver> = new Map();

    private v2Identifier: dataSyncProtocol.IResourceIdentifier;

    constructor(
        client: HiveClient,
        options: {
            guid: string;
            contentId: string;
            path: string;
            pathParams: Record<string, string>;
            /** Type of the main data in the document. Defaults to Y.Map */
            YType?: typeof Y.AbstractType<any>;
            /** Is the client in read-only mode and local edit should be blocked? */
            readOnly?: boolean;
            /** A browser cache to use alongside the data sync. */
            cache?: HiveDataSyncBrowserCache;
            /** The initial Y.Doc - only use for testing. */
            dangerouslySetInitialDoc?: Y.Doc;
        }
    ) {
        const { YType = Y.Map, readOnly = false, guid, contentId, path, pathParams } = options;

        if (
            process.env.NODE_ENV === 'production' &&
            typeof globalThis.window === 'undefined' &&
            !client.dangerouslyAllowWebsocket
        ) {
            throw new Error(
                'HiveDataSyncClient can only be used in the browser, or in node process when running tests'
            );
        }

        this.guid = guid;
        this.contentId = contentId;
        this.path = path;
        this.pathParams = pathParams;
        this.logger = Logger('hive:datasync', { prefix: `[${path}]` });
        this.client = client;

        this.v2Identifier = getIdentifierFromPath(
            getContentResourcePath(contentId, path, pathParams)
        );

        this.doc = (() => {
            if (options.dangerouslySetInitialDoc) {
                options.dangerouslySetInitialDoc.guid = this.guid;
                options.dangerouslySetInitialDoc.meta = {
                    ySessionId: options.dangerouslySetInitialDoc.meta?.ySessionId ?? undefined,
                };
                return options.dangerouslySetInitialDoc;
            }

            return new Y.Doc({ guid, meta: { ySessionId: undefined } });
        })();

        this.presences = [];
        this.YType = YType;
        this.#clientReadOnly = readOnly;
        this.cache = options.cache;

        // Register the doc immediately with the cache to allow pre-fetching.
        // this.cache?.registerDoc(this.doc);

        const data = this.doc.get('data', YType);

        this.undoManager = new Y.UndoManager(data, {
            trackedOrigins: new Set([HiveDataSyncUpdateOrigin.LOCAL]),
        });

        this.undoManager.on('stack-item-added', (event) => {
            event.stackItem.meta.set('positionsBefore', this.beforeTransactionPresence?.positions);
            event.stackItem.meta.set('positions', this.currentPresence?.positions);
        });

        this.undoManager.on('stack-item-updated', (event) => {
            event.stackItem.meta.set('positions', this.currentPresence?.positions);
        });

        this.undoManager.on('stack-item-popped', (event) => {
            // TODO: Change once https://github.com/yjs/yjs/issues/353 is resolved
            const inverseStack =
                event.type === 'undo' ? this.undoManager.redoStack : this.undoManager.undoStack;
            const inverseItem = inverseStack[inverseStack.length - 1];
            if (inverseItem) {
                inverseItem.meta.set('positions', event.stackItem.meta.get('positionsBefore'));
                inverseItem.meta.set('positionsBefore', event.stackItem.meta.get('positions'));
            }

            const positions = event.stackItem.meta.get('positionsBefore');
            if (positions) {
                this.events.emit('restorePositions', positions);
            }
        });

        this.doc.on('updateV2', (update, origin) => {
            // Don't propagate updates from the cache to the server, we do this once handshake is done.
            if (origin === HiveDataSyncCacheOrigin) {
                return;
            }

            this.pushUpdateToLog(update, origin);

            if (origin !== HiveDataSyncUpdateOrigin.REMOTE) {
                if (this.dangerouslyPaused) {
                    this.dangerouslyPaused.push(update);
                } else {
                    this.batchUpdate(update);
                }
            }

            this.logger.info('call update listeners');
            this.events.emit('update', origin);
        });

        this.#markersClient = this.withRPC('Markers')[0];
    }

    /**
     * Handle a change in the websocket state.
     */
    private onWebSocketState(wsState: WebSocketState) {
        // An errored datasync is going to stay errored.
        if (this.state === HiveDataSyncState.ERRORED) {
            return;
        }

        let newState = HiveDataSyncState.CLOSED;
        switch (wsState) {
            case WebSocketState.CONNECTING:
                newState = HiveDataSyncState.CONNECTING;
                break;
            case WebSocketState.OPEN:
                if (this.doc.isSynced) {
                    newState = HiveDataSyncState.OPEN;
                } else {
                    newState = HiveDataSyncState.HANDSHAKING;
                }
                break;
            case WebSocketState.ERRORED:
                newState = HiveDataSyncState.ERRORED;
                break;
        }

        this.state = newState;
    }

    /**
     * Returns the current state of the data sync.
     */
    get state() {
        return this.#state;
    }

    /**
     * Set the state of the data sync. Handles any events that need to be emitted.
     */
    set state(newState: HiveDataSyncState) {
        const previousState = this.#state;
        this.#state = newState;

        const docIsSynced = newState === HiveDataSyncState.OPEN;
        if (docIsSynced !== this.doc.isSynced) {
            this.doc.emit('sync', [docIsSynced, this.doc]);
        }

        if (newState !== previousState) {
            this.logger.info(`state change: ${previousState} -> ${newState}`);
            this.events.emit('state', newState);
        }
    }

    /**
     * Return the presence of the current client.
     */
    public get presence(): HiveDataSyncCurrentPresence<TPresence> | null {
        return this.currentPresence || null;
    }

    /**
     * Return the dataSync client read-only mode.
     */
    public get readOnly(): boolean {
        return this.#clientReadOnly || this.#serverReadOnly;
    }

    /**
     * Return the read-only mode set on the client.
     */
    public get clientReadOnly(): boolean {
        return this.#clientReadOnly;
    }

    /**
     * Return the read-only mode enforced by the server.
     */
    public get serverReadOnly(): boolean {
        return this.#serverReadOnly;
    }

    /**
     * Return whether the client needs to normalize updates from the server.
     */
    public get needsClientSideNormalization(): boolean {
        return this.#needsClientSideNormalization;
    }

    /**
     * Set the client read-only mode (on or off).
     *
     * If going into read-only mode, this fn will flush any pending updates
     * and return a Promise for when they've been acknowledged by the server.
     */
    public async setReadOnly(clientReadOnly: boolean): Promise<MessageSyncError[]> {
        // No change to read-only prop, exit early.
        if (this.#clientReadOnly === clientReadOnly) {
            return [];
        }

        const previousReadOnly = this.readOnly;

        // Flush any pending updates before we go read-only.
        const resolve: Promise<MessageSyncError[]> = clientReadOnly
            ? this.flushBatchUpdates()
            : Promise.resolve([]);

        const errors = await resolve;
        this.#clientReadOnly = clientReadOnly;

        if (previousReadOnly !== this.readOnly) {
            this.events.emit('readOnly', this.readOnly);
        }
        return errors;
    }

    private setServerReadOnly(serverReadOnly: boolean) {
        if (this.#serverReadOnly === serverReadOnly) {
            return;
        }

        const previousReadOnly = this.readOnly;
        this.#serverReadOnly = serverReadOnly;

        if (previousReadOnly !== this.readOnly) {
            this.events.emit('readOnly', this.readOnly);
        }
    }

    /**
     * Return a clock, which changes with every mutation of the underlying doc.
     */
    get clock(): number | undefined {
        const data = this.doc.get('data', this.YType) as YJSInput;
        return getYMutationClock(data);
    }

    /**
     * True if the data sync is closed and can't be used.
     */
    get isClosed() {
        return this.state === HiveDataSyncState.CLOSED || this.state === HiveDataSyncState.ERRORED;
    }

    /**
     * Return the subscriptionId if using V2 socket.
     */
    private get subscriptionId(): number | undefined {
        return this.socket && 'subscriptionId' in this.socket
            ? this.socket.subscriptionId
            : undefined;
    }

    /**
     * Initialize the socket by reading its current data and start listening.
     * Returns a promise when the initial handshake has been completed.
     * Returns undefined if the data sync is already open.
     */
    public open(queryParams: Record<string, string> = {}): void | Promise<void> {
        this.assertNotDestroyed();

        switch (this.state) {
            case HiveDataSyncState.OPEN:
            case HiveDataSyncState.HANDSHAKING:
            case HiveDataSyncState.CONNECTING:
                return this.waitForState(HiveDataSyncState.OPEN);
            case HiveDataSyncState.ERRORED:
                return Promise.reject(new Error('Connection failed to establish with the server'));
            case HiveDataSyncState.INACTIVE:
                // Socket was closed due to inactivity — do not reopen
                return;
            case HiveDataSyncState.CLOSED: {
                // Close the socket, cancelling any reconnection, and directly open a new one
                const toClose = this.socket;
                this.socket = null;
                toClose?.destroy();
                break;
            }
            default:
                assertNever(this.state);
        }

        this.logger.info('opening datasync socket', this.path, this.pathParams, queryParams);

        const openWithMode = (resolvedUseV2: boolean): void | Promise<void> => {
            const decoderHandlers: DataSyncDecoderHandlers = {
                promptInit: (message) => {
                    assertIsDefined(message.stateVector);

                    if (this.state === HiveDataSyncState.ERRORED) {
                        this.logger.info(
                            'ignoring promptInit from server as we are in error state'
                        );
                        return;
                    }

                    const ySessionId = (this.doc.meta.ySessionId ?? null) as number | null;
                    this.logger.info(
                        `received prompt init with ySessionId ${message.ySessionId}${ySessionId ? ` stored: ${ySessionId}` : ''}`
                    );

                    // TODO: Enable once we are calling registerDoc.
                    // When the server prompts us to init, it provides a ySessionId identifying the common history of the Ydoc.
                    // If we have loaded a yDoc that doesn't match this ySessionId, we're in a corrupt state.
                    // if (ySessionId !== null && message.ySessionId !== ySessionId) {
                    //     this.state = HiveDataSyncState.ERRORED;
                    //     this.cache
                    //         ?.resetAfterMismatch({
                    //             guid: this.guid,
                    //             ySessionId: message.ySessionId,
                    //         })
                    //         .then(() => {
                    //             if (typeof window !== 'undefined') {
                    //                 window.location.reload();
                    //             }
                    //         });

                    //     return;
                    // }

                    this.doc.meta.ySessionId = message.ySessionId;
                    this.sendInit(message.stateVector);
                },
                init: (message) => {
                    const { update, version } = message;
                    assertIsDefined(update);

                    if (this.state === HiveDataSyncState.ERRORED) {
                        this.logger.info('ignoring init from server as we are in error state');
                        return;
                    }

                    this.logger.info('received init from server');
                    this.setServerReadOnly(Boolean(message.readOnly));
                    this.#needsClientSideNormalization = Boolean(message.normalized) === false;
                    applyVersionedUpdate(
                        this.doc,
                        version,
                        update,
                        HiveDataSyncUpdateOrigin.REMOTE
                    );

                    // Once the init has been received, the data sync is open and ready for business.
                    this.state = HiveDataSyncState.OPEN;
                },
                update: (message) => {
                    const { id, update, version } = message;
                    assertIsDefined(update);

                    if (this.state === HiveDataSyncState.ERRORED) {
                        this.logger.info('ignoring update from server as we are in error state');
                        return;
                    }

                    this.logger.info(
                        `received update ${id} from server (size: ${update.byteLength})`
                    );
                    applyVersionedUpdate(
                        this.doc,
                        version,
                        update,
                        HiveDataSyncUpdateOrigin.REMOTE
                    );
                },
                updatePresence: () => {},
                broadcastOperation: (message) => {
                    const operation = requireContentOperation(message.operation);

                    const nextOperation: ContentOperation<{ [key: string]: string }> = {
                        id: operation.id,
                        status: operation.status,
                        payload: operation.payload || {},
                        createdAt: Number(operation.createdAt),
                        updatedAt: Number(operation.updatedAt),
                        updateId: operation.updateId,
                        expireAfter: operation.expireAfter,
                        cursor: operation.cursor,
                        target: operation.target,
                    };
                    this.operation = nextOperation;
                    this.events.emit('operation', nextOperation);
                },
                broadcastPresences: (message) => {
                    this.logger.info('received broadcast presences message');

                    const previousClientId = this.clientId;
                    this.clientId = message.selfClientId ?? null;
                    if (message.selfClientId && previousClientId !== message.selfClientId) {
                        this.logger.info('client id changed to', message.selfClientId);
                        this.events.emit('clientId', message.selfClientId);
                    }

                    this.presences = Object.values(message.presences || {})
                        .map((presence) => {
                            const validPresence = requirePresence(presence);

                            return {
                                clientId: validPresence.clientId,
                                joinTimestamp: Number(validPresence.joinTimestamp),
                                updateTimestamp: Number(validPresence.updateTimestamp),
                                data: (validPresence.data || {}) as TPresence,
                                positions: Object.fromEntries(
                                    Object.entries(validPresence.positions || {}).map(
                                        ([key, value]) => [key, Y.decodeRelativePosition(value)]
                                    )
                                ),
                            };
                        })
                        .sort((a, b) => {
                            return a.joinTimestamp - b.joinTimestamp;
                        });

                    this.events.emit('presences');
                },
                ack: (message) => {
                    assertIsDefined(message.message);
                    this.ackMessage(message.message);
                },
                rpcRequest: () => {
                    // Should not get it from the server
                },
                rpcReply: (message) => {
                    for (const client of this.rpcClients) {
                        if (client.onReplyMessage(message)) {
                            return;
                        }
                    }
                },
                broadcastMarkers: (message) => {
                    this.logger.info('received broadcast content markers message');
                    this.markers =
                        message.markers?.map((marker) => {
                            const validMarker = requireContentMarker(marker);

                            return {
                                id: validMarker.markerId,
                                type: validMarker.type,
                                data: validMarker.data as TMarkerData,
                                author: validMarker.author,
                                anchor: Y.decodeRelativePosition(
                                    validMarker.anchorRelativePosition
                                ),
                                focus: Y.decodeRelativePosition(validMarker.focusRelativePosition),
                                content: validMarker.content || null,
                            };
                        }) ?? [];

                    this.events.emit('markers');
                },
            };

            const socket = resolvedUseV2
                ? this.client.getV2Socket(
                      this.contentId,
                      this.path,
                      this.pathParams,
                      decoderHandlers
                  )
                : this.client.getSocket(
                      this.contentId,
                      this.path,
                      this.pathParams,
                      decoderHandlers,
                      queryParams
                  );
            this.socket = socket;

            /**
             * Create an encoder with ack support.
             */
            this.encoder = createDataSyncEncoder(({ chunk, message, chunkIndex }) => {
                const messageId = getMessageId(message);

                // On the first chunk, we need to create a resolver for the message.
                // Individual chunks are not acknowledged, but the overall message is.
                if (chunkIndex === 0 && messageRequiresAck(message)) {
                    // Messages should only be sent once.
                    if (this.messageResolvers.has(messageId)) {
                        throw new Error(`message ${messageId} already has a resolver`);
                    }

                    // TODO: Promise.withResolvers
                    // @ts-expect-error we are starting with a partial object
                    const resolver: MessageResolver = {
                        isResolved: false,
                    };

                    resolver.promise = new Promise<MessageSyncError | undefined>((resolve) => {
                        resolver.resolve = (err?: string) => {
                            if (resolver.isResolved) {
                                return;
                            }

                            resolver.isResolved = true;
                            this.messageResolvers.delete(messageId);

                            if (err) {
                                const [messageType] = Object.keys(message);
                                const error = new MessageSyncError(err, {
                                    guid: this.guid,
                                    messageId,
                                    clientId: this.clientId || '',
                                    messageType,
                                });

                                this.logger.warning(error.toString());
                                resolve(error);
                            } else {
                                this.logger.info(`ack message ${messageId}`);
                                resolve(undefined);
                            }
                        };
                    });

                    // Update messages are resolved if the websocket reconnects and a handshake occurs.
                    if ('update' in message && Boolean(message.update)) {
                        const whenConnecting =
                            this.waitForState(HiveDataSyncState.HANDSHAKING) ?? Promise.resolve();

                        whenConnecting
                            .then(() => this.waitForState(HiveDataSyncState.OPEN))
                            .then(() => resolver.resolve());
                    }

                    this.messageResolvers.set(messageId, resolver);
                }

                socket.send(chunk);
            });

            /**
             * Listen for updates on the underlying WebSocket and adjust the DataSync state accordingly.
             */
            socket.listen('state', (state) => {
                // If the socket was closed due to inactivity, transition to INACTIVE state
                if (state === WebSocketState.CLOSED && 'inactive' in socket && socket.inactive) {
                    this.state = HiveDataSyncState.INACTIVE;
                    return;
                }

                this.onWebSocketState(state);

                if (state === WebSocketState.OPEN) {
                    const traceSync = new Trace(`hive:datasync:sync:${this.path}`);
                    traceSync.setLabels(this.pathParams);
                    traceSync.wrap(async () => {
                        // When connection is established, we ask the server to send the init message
                        // It can happened multiple times if connection drops and is re-established
                        this.sendPromptInit();

                        // wait for the data sync to be open to measure the trace.
                        await this.waitForState(HiveDataSyncState.OPEN);
                    });
                    this.sendPresence();
                }
            });

            const traceOpen = new Trace(`hive:datasync:open:${this.path}`);
            traceOpen.setLabels(this.pathParams);
            traceOpen
                .wrap(async () => {
                    await socket.open();
                })
                .catch((error) => {
                    this.logger.error('failed to open datasync socket', error);
                    reportException(error);
                });

            return this.waitForState(HiveDataSyncState.OPEN);
        };

        this.state = HiveDataSyncState.CONNECTING;
        return (async () => {
            const resolvedUseV2Promise = this.client.resolveIsHiveV2(this.contentId);
            if (!resolvedUseV2Promise) {
                this.state = HiveDataSyncState.ERRORED;
                throw new Error(
                    "Hive DataSync requires 'isHiveV2'. Provide isHiveV2 when creating HiveClient."
                );
            }

            try {
                const resolvedUseV2 = await resolvedUseV2Promise;
                await openWithMode(resolvedUseV2);
            } catch (error) {
                this.state = HiveDataSyncState.ERRORED;
                throw error;
            }
        })();
    }

    /**
     * Wait for the data sync to into the specified state.
     * Returns a promise if the data sync is not in the wanted state.
     * Returns undefined if the data sync is already in the wanted state.
     */
    public waitForState(expectedState: HiveDataSyncState): undefined | Promise<void> {
        if (this.state === expectedState) {
            return;
        }

        const existingPromise = this.waitForStatePromises.get(expectedState);
        if (existingPromise) {
            return existingPromise;
        }

        const waitPromise = new Promise<void>((resolve) => {
            const onStateChange = (state: HiveDataSyncState) => {
                if (state === expectedState) {
                    this.events.off('state', onStateChange);
                    this.waitForStatePromises.delete(expectedState);
                    resolve();
                }
            };

            this.events.on('state', onStateChange);
        });

        this.waitForStatePromises.set(expectedState, waitPromise);
        return waitPromise;
    }

    /**
     * Close the connection. It can be re-opened later.
     */
    public close() {
        this.assertNotDestroyed();

        if (!this.socket) {
            this.logger.info('closing datasync, but socket is already closed');
            return;
        }

        this.logger.info('closing datasync socket');
        this.socket.destroy();
        this.socket = null;
        this.encoder = null;
    }

    /**
     * Destroy the connection. It cannot be re-opened later and needs to be recreated.
     */
    public destroy() {
        if (this.destroyed) {
            return;
        }

        this.close();
        this.batchDebounceFlush?.cancel();
        this.undoManager.destroy();
        this.doc.destroy();
        this.events.removeAllListeners();
        this.waitForStatePromises.clear();
        this.destroyed = true;
    }

    /**
     * Add a callback to be called when the document is updated.
     */
    public listen(event: 'update', callback: (origin: string) => void): () => void;
    public listen(event: 'readOnly', callback: (readOnly: boolean) => void): () => void;
    public listen(event: 'presences', callback: () => void): () => void;
    public listen(event: 'markers', callback: () => void): () => void;
    public listen(event: 'operation', callback: (operation: ContentOperation) => void): () => void;
    public listen(event: 'clientId', callback: (clientId: string) => void): () => void;
    public listen(event: 'state', callback: (state: HiveDataSyncState) => void): () => void;
    public listen(
        event: 'update' | 'presences' | 'state' | 'operation' | 'markers',
        callback: () => void
    ): () => void;
    public listen(
        event: 'restorePositions',
        callback: (positions: Record<string, Y.RelativePosition>) => void
    ): () => void;
    public listen(
        event:
            | 'update'
            | 'presences'
            | 'markers'
            | 'state'
            | 'restorePositions'
            | 'clientId'
            | 'operation'
            | 'readOnly',
        callback: (value: any) => void
    ): () => void {
        this.assertNotDestroyed();

        this.events.addListener(event, callback);
        return () => {
            this.events.removeListener(event, callback);
        };
    }

    /**
     * Wait for all currently pending updates to be sent and ancknowledged by the server.
     *
     * It returns an array of all errors that happened when waiting for sync. If the
     * array if empty, entire sync happened successfully.
     */
    public async waitForSync(): Promise<MessageSyncError[]> {
        this.assertNotDestroyed();

        if (this.messageResolvers.size === 0) {
            return [];
        }

        const keys = Array.from(this.messageResolvers.keys());
        const promises = keys.map((key) => {
            const resolver = this.messageResolvers.get(key);
            assertIsDefined(resolver);
            return resolver.promise;
        });
        this.logger.info(`waiting for sync of ${keys.length} messages`);
        const errors = (await Promise.all(promises)).filter(filterOutNullable);
        this.logger.info(
            `waiting for sync finished, there were ${errors.length} errors from ${keys.length} messages`
        );
        return errors;
    }

    /**
     * Execute a transaction on the document.
     */
    public transact<Return>(callback: (doc: Y.Doc) => Return): Return {
        this.assertNotDestroyed();

        this.logger.info('start transaction on document');
        this.beforeTransactionPresence = this.currentPresence;
        return this.doc.transact(() => {
            return callback(this.doc);
        }, HiveDataSyncUpdateOrigin.LOCAL);
    }

    /**
     * Update the presence for the current client.
     */
    public setPresence(presence: {
        data?: TPresence | Record<string, unknown>;
        positions?: Record<string, Y.AbsolutePosition | undefined>;
    }) {
        this.assertNotDestroyed();

        this.logger.info('set presence', presence);
        this.currentPresence = {
            // @ts-ignore
            data: Object.fromEntries(
                Object.entries(presence.data || this.currentPresence?.data || {})
                    // Remove undefined values as they are not allowed in the protocol
                    .filter(([_key, value]) => value !== undefined)
            ),
            positions: Object.fromEntries(
                Object.entries(presence.positions || {})
                    .map(([key, pos]) =>
                        pos
                            ? [
                                  key,
                                  Y.createRelativePositionFromTypeIndex(
                                      pos.type,
                                      pos.index,
                                      pos.assoc
                                  ),
                              ]
                            : null
                    )
                    .filter(filterOutNullable)
            ),
        };
        this.sendPresence();
    }

    /**
     * Undo the last changes made locally to the document.
     */
    public undo() {
        this.undoManager.undo();
    }

    /**
     * Redo the last changes made locally to the document.
     */
    public redo() {
        this.undoManager.redo();
    }

    /**
     * Ensure that the next operation that is put on the local history is not merged with the previous operation.
     */
    public stopCapturing() {
        this.undoManager.stopCapturing();
    }

    /**
     * Return true if batching of updates is enabled.
     */
    public hasBatchingEnabled() {
        return Boolean(this.batchDebounceFlush);
    }

    /**
     * Return true if the data sync has batched updates that have not yet been sent to the server.
     */
    public hasBatchedUpdates() {
        return this.batchUpdates.length > 0;
    }

    /**
     * Return true if the data sync has unsynced changes that have not yet been acknowledged by the server.
     */
    public hasUnsyncedUpdates() {
        return this.messageResolvers.size > 0;
    }

    /**
     * Configure the interval to send the batch of updates to the server.
     */
    public resetBatchingInterval(wait: number, maxWait: number = wait * 5) {
        if (!wait) {
            void this.flushBatchUpdates();
            this.batchDebounceFlush = undefined;
        } else {
            this.batchDebounceFlush?.cancel();
            this.batchDebounceFlush = debounce(
                () => {
                    void this.flushBatchUpdates();
                },
                wait,
                { maxWait }
            );
        }
    }

    /**
     * Disable the batching of updates to the server.
     */
    public disableBatching() {
        this.resetBatchingInterval(0);
    }

    /**
     * Send all batch updates to the server.
     *
     * It returns a promise that can be awaited for the flushed updates to be
     * ack'd by the server.
     */
    public flushBatchUpdates(): Promise<MessageSyncError[]> {
        if (this.readOnly) {
            // Always wait for sync in case we're waiting for the ACK of a message that
            // was sent before the read-only mode was enabled.
            return this.waitForSync();
        }

        this.batchDebounceFlush?.cancel();

        if (this.batchUpdates.length === 0) {
            // Always wait for sync in case we're waiting for the ACK of a message.
            return this.waitForSync();
        }

        const updates = this.batchUpdates;
        this.batchUpdates = [];

        const mergedUpdates = Y.mergeUpdatesV2(updates);
        this.sendUpdate(mergedUpdates);
        return this.waitForSync();
    }

    /**
     * Create and send a new content marker to the server.
     */
    public async createMarker(marker: {
        type: dataSyncProtocol.ContentMarkerType;
        anchor: Y.RelativePosition;
        focus: Y.RelativePosition;
        content?: Uint8Array;
        data: TMarkerData;
    }) {
        this.logger.info('creating content marker', marker.type);
        const result = await this.#markersClient.createMarker({
            type: marker.type,
            content: marker.content,
            data: marker.data,
            anchorRelativePosition: Y.encodeRelativePosition(marker.anchor),
            focusRelativePosition: Y.encodeRelativePosition(marker.focus),
        });

        assertIsDefined(result.markerId);
        return result.markerId;
    }

    /**
     * Remove a content marker on the server.
     */
    public async removeMarker(markerId: string) {
        this.logger.info('removing content marker', markerId);
        await this.#markersClient.removeMarker({
            markerId,
        });
    }

    private sendPresence() {
        if (!this.encoder || !this.currentPresence) {
            return;
        }

        this.logger.info('sending presence to server');
        this.encoder.write({
            updatePresence: {
                id: this.encoder.newMessageID(),
                data: this.currentPresence.data,
                positions: Object.fromEntries(
                    Object.entries(this.currentPresence.positions).map(([key, pos]) => [
                        key,
                        Y.encodeRelativePosition(pos),
                    ])
                ),
                subscriptionId: this.subscriptionId,
            },
        });
    }

    private sendPromptInit() {
        if (!this.encoder) {
            return;
        }

        this.logger.info('sending prompt to init to server');
        this.encoder.write({
            promptInit: {
                id: this.encoder.newMessageID(),
                stateVector: Y.encodeStateVector(this.doc),
                version: UpdateFormatVersion.V2,
                subscriptionId: this.subscriptionId,
                // The client's ySessionId is ignored by the server.
                ySessionId: 0,
            },
        });
    }

    private sendInit(serverStateVector: Uint8Array | undefined) {
        if (!this.encoder || this.readOnly) {
            return;
        }

        this.logger.info('sending init to server');
        this.encoder.write({
            init: {
                id: this.encoder.newMessageID(),
                update: Y.encodeStateAsUpdateV2(this.doc, serverStateVector),
                version: UpdateFormatVersion.V2,
                subscriptionId: this.subscriptionId,
                // not sent by client for now
                ySessionId: 0,
            },
        });
    }

    private batchUpdate(update: Uint8Array) {
        if (this.readOnly) {
            return;
        }

        if (!this.batchDebounceFlush) {
            this.sendUpdate(update);
            return;
        }

        this.batchUpdates.push(update);
        this.batchDebounceFlush();
    }

    private sendUpdate(update: Uint8Array) {
        if (!this.encoder || this.readOnly) {
            return;
        }

        const id = this.encoder.newMessageID();
        this.logger.info(`sending update ${id} to server (size: ${update.byteLength})`);
        this.encoder.write({
            update: {
                id,
                update,
                version: UpdateFormatVersion.V2,
                ySessionId: this.doc.meta.ySessionId,
                subscriptionId: this.subscriptionId,
            },
        });
    }

    /**
     * Mark a message as acknowledged by the server.
     */
    private ackMessage(messageId: number) {
        const resolver = this.messageResolvers.get(messageId);

        if (resolver) {
            resolver.resolve();
        }
    }

    /**
     * Assert that the sync if not destroyed.
     */
    private assertNotDestroyed() {
        if (this.destroyed) {
            throw new Error('datasync is destroyed');
        }
    }

    /**
     * SHOULD ONLY BE USED FOR TESTING.
     * Pause the sync, and buffer all updates. The updates will be sent when `dangerouslyResumeSync` is called.
     */
    public dangerouslyPauseSync() {
        if (process.env.NODE_ENV === 'production') {
            throw new Error('dangerouslyPauseSync should only be used for testing and development');
        }
        this.dangerouslyPaused = [];
    }

    /**
     * SHOULD ONLY BE USED FOR TESTING.
     * Resume the sync, and send all buffered updates.
     */
    public async dangerouslyResumeSync() {
        if (process.env.NODE_ENV === 'production') {
            throw new Error('dangerouslyPauseSync should only be used for testing and development');
        }

        if (!this.dangerouslyPaused) {
            return;
        }

        const updates = this.dangerouslyPaused;
        this.dangerouslyPaused = undefined;

        updates.forEach((update) => {
            this.sendUpdate(update);
        });

        return this.waitForSync();
    }

    /**
     * Configure a RPC client to send RPC requests to the server.
     * @returns A tuple with the service and a function to dispose the client.
     */
    public withRPC<Name extends DataSyncRPCServiceName>(serviceName: Name) {
        const client = createDataSyncRPCClient(
            serviceName,
            {
                newMessageID: () => {
                    assertIsDefined(this.encoder);
                    return this.encoder.newMessageID();
                },
                write: (message) => {
                    assertIsDefined(this.encoder);

                    // Acks are required for RPC but a handshake cannot guarantee to resolve them.
                    this.encoder.write(message);
                },
            },
            {
                subscriptionId: () => this.subscriptionId,
            }
        );
        this.rpcClients.push(client);
        return [
            client.service,
            () => {
                this.rpcClients.splice(this.rpcClients.indexOf(client), 1);
            },
        ] as const;
    }

    /**
     * Push an update to the log of updates applied to the document.
     */
    public pushUpdateToLog(update: Uint8Array, origin: HiveDataSyncUpdateOrigin) {
        this.updateLog.push({
            timestamp: Date.now(),
            update,
            origin: typeof origin === 'object' ? JSON.stringify(origin) : String(origin),
        });
    }

    /**
     * Get the log of updates applied to the document as a Uint8Array.
     */
    public getUpdateLog(): Uint8Array {
        return dataSyncProtocol.DebugLog.encode({
            updates: this.updateLog.map((update) => ({
                timestamp: update.timestamp,
                update: update.update,
                origin: update.origin,
            })),
        }).finish();
    }
}

/**
 * Error thrown when a message is not acknowledged by the server.
 */
export class MessageSyncError extends Error {
    public guid: string;
    public messageId: number;
    public clientId: string;
    public messageType: string;

    constructor(
        message: string,
        metadata: {
            guid: string;
            messageId: number;
            clientId: string;
            messageType: string;
        }
    ) {
        super(message);

        this.guid = metadata.guid;
        this.messageId = metadata.messageId;
        this.clientId = metadata.clientId;
        this.messageType = metadata.messageType;
    }

    public toString(): string {
        return `MessageSyncError: ${this.message} guid=${this.guid} messageType=${this.messageType} messageId=${this.messageId} clientId=${this.clientId}`;
    }
}

/**
 * A stateful resolver for an individual message across the data sync.
 */
type MessageResolver = {
    /**
     * True if the message has been resolved (with or without error)
     */
    isResolved: boolean;

    /**
     * The underlying promise of the message.
     */
    promise: Promise<MessageSyncError | undefined>;

    /**
     * Resolve the message, optionally with an error.
     */
    resolve: (err?: string) => void;
};

/**
 * Updates and RPC requests require an ack from the server.
 */
function messageRequiresAck(message: dataSyncProtocol.IMessage): boolean {
    return (
        ('update' in message && Boolean(message.update)) ||
        ('rpcRequest' in message && Boolean(message.rpcRequest))
    );
}

function getIdentifierFromPath(path: string): dataSyncProtocol.IResourceIdentifier {
    const branchRegex = /\/branches\/(?<branchName>[^/]+)(?:\/|$)/;
    const branchMatch = path.match(branchRegex);
    const branchName = branchMatch?.groups?.branchName ?? undefined;

    const pagesRegex = /\/pages\/(?<pageId>[^/]+)(?:\/|$)/;
    const reucRegex = /\/reusable-contents\/(?<reucId>[^/]+)(?:\/|$)/;

    const pageMatch = path.match(pagesRegex);
    const reucMatch = path.match(reucRegex);

    const pageId = pageMatch?.groups?.pageId ?? undefined;
    const reusableContentId = reucMatch?.groups?.reucId ?? undefined;

    return (() => {
        assert(branchName, 'no branch name');

        if (pageId) {
            return {
                type: dataSyncProtocol.ResourceType.PAGE,
                id: pageId,
                branchId: branchName,
            };
        }

        if (reusableContentId) {
            return {
                type: dataSyncProtocol.ResourceType.REUSABLE_CONTENT,
                id: reusableContentId,
                branchId: branchName,
            };
        }

        return { type: dataSyncProtocol.ResourceType.BRANCH, branchId: branchName };
    })();
}

/**
 * Return the content resource full path.
 */
function getContentResourcePath(
    contentId: string,
    path: string,
    params: Record<string, string>,
    stripContentId = false
) {
    for (const [key, value] of Object.entries(params)) {
        path = path.replace(`:${key}`, value);
    }

    if (stripContentId) {
        return path;
    }

    return `content/${contentId}${path}`;
}
